//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\HR\Pages\AssessmentList.jsx
import React, { useEffect, useState } from "react";
import {
    Search,
   
    FileText,
    Users,
    BarChart3,
    CheckCircle2,   
    Mail,
    Activity,
    Clock,   
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Monitor,
    UserPlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAssignedAssessmentListByHRUserID, createAssessmentTemplate } from "../Services/CompanyServices";
import { isAuthRedirectInProgress } from "../../../Utility/authSession";
import image from "../../../assests/NewLoginVectorImage1.png";
import ViewAssessmentModal from "../Modal/ViewAssessmentModal";
import PublishResponderModal from "../Modal/PublishResponderModal";
// import SendSurveyMailModal from "../../Survey/Modal/SendSurveyMailModal";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../Utility/sanitizeHtml";
import SendAssessmentMailModal from "../Modal/SendAssessmentMailModal";

const STATUS_TABS = [
    { key: "Active", label: "Active", icon: Activity },
    { key: "Scheduled", label: "Scheduled", icon: Clock },
    { key: "Completed", label: "Completed", icon: CheckCircle2 },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const deriveStatus = (startDateTime, endDateTime) => {
    const now = new Date();
    const start = startDateTime ? new Date(startDateTime) : null;
    const end = endDateTime ? new Date(endDateTime) : null;
    if (end && now > end) return "Completed";
    if (start && now < start) return "Scheduled";
    return "Active";
};

const formatDateParts = (value) => {
    if (!value) return { date: "-", time: "" };
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return { date: value, time: "" };
    return {
        date: parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        time: parsed.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
    };
};

const getStatusColor = (status) => {
    switch (status) {
        case "Active":
            return "bg-green-100 text-green-700";
        case "Scheduled":
            return "bg-purple-100 text-purple-700";
        case "Completed":
            return "bg-blue-100 text-blue-700";
        default:
            return "bg-gray-100 text-gray-700";
    }
};

const getPageNumbers = (current, total) => {
    const pages = [];
    for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
            pages.push(i);
        } else if (pages[pages.length - 1] !== "...") {
            pages.push("...");
        }
    }
    return pages;
};

const StatCardSkeleton = () => (
    <div className="bg-white rounded-2xl px-4 py-4 shadow-md border border-gray-100 animate-pulse">
        <div className="h-9 w-9 bg-gray-100 rounded-xl mb-3" />
        <div className="h-3 w-20 bg-gray-100 rounded mb-2" />
        <div className="h-6 w-12 bg-gray-100 rounded" />
    </div>
);

const RowSkeleton = () => (
    <tr className="border-b border-gray-50">
        {Array.from({ length: 6 }).map((_, i) => (
            <td key={i} className="px-4 py-4">
                <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
            </td>
        ))}
    </tr>
);

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, subtitle }) => (
    <div className="bg-white rounded-2xl px-4 py-4 shadow-md border border-gray-100">
        <div className="flex items-start gap-3">
            <div className={`${iconBg} p-2.5 rounded-xl shrink-0`}>
                <Icon className={iconColor} size={20} />
            </div>
            <div>
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <h2 className="text-2xl font-bold text-gray-900 mt-0.5">{value}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            </div>
        </div>
    </div>
);



const AssessmentList = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState("Active");
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [viewAssessment, setViewAssessment] = useState(null);
    const [responderAssessment, setResponderAssessment] = useState(null);
    const [mailAssessment, setMailAssessment] = useState(null);

    useEffect(() => {
        loadAssessments();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, search]);

    const loadAssessments = async () => {
        try {
            setLoading(true);
            const response = await getAssignedAssessmentListByHRUserID();
            if (response.data.status?.toLowerCase() === "success") {
                setAssessments(response.data.listOfAssessment || []);
            } else {
                CustomToast("fail", response.data.message);
            }
        } catch (error) {
            if (!isAuthRedirectInProgress()) {
                CustomToast("fail", error.response?.data?.message || "Unable to load assigned assessments.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSendMail = async (payload) => {
        const requestBody = {
            assessmentUID: mailAssessment?.assessmentUID,
            templateTypeID: Number(payload.templateTypeID),
            subject: payload.subject,
            body: payload.body,
        };

        const response = await createAssessmentTemplate(requestBody);
        if (response.data.status?.toLowerCase() !== "success") {
            CustomToast("fail", response.data.message || "Unable to save mail template.");
            throw new Error(response.data.message);
        }
        CustomToast("success", "Mail template saved successfully.");
    };

    const withStatus = assessments.map((a) => ({
        ...a,
        _status: deriveStatus(a.startDateTime, a.endDateTime),
    }));

    const filtered = withStatus.filter(
        (a) =>
            a._status === activeTab &&
            a.assessmentName?.toLowerCase().includes(search.toLowerCase())
    );

    const getTabCount = (tabKey) => withStatus.filter((a) => a._status === tabKey).length;
    const totalResponses = withStatus.reduce((sum, a) => sum + (a.totalSubmittedResponses || 0), 0);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + pageSize);

    // TODO: point this at your actual take-assessment route (SurveyResponder.jsx)
    // const handleStart = (assessment) => {
    //     navigate("/dashboard/Take-Assessment", {
    //         state: { assessmentUID: assessment.assessmentUID, assessmentName: assessment.assessmentName },
    //     });
    // };

    const handlePreview = (assessment) => {
        navigate(`/Company/dashboard/my-assessments/preview/${assessment.assessmentUID}`, {
            state: {
                assessmentUID: assessment.assessmentUID,
                assessmentName: assessment.assessmentName,
                description: assessment.description,
            }
        });
    };

    const mappedAssessment = viewAssessment ? {
        ...viewAssessment,
        surveyName: viewAssessment.assessmentName,
        status: viewAssessment._status,
        surveyDescription: viewAssessment.description,
        totalQuestions: viewAssessment.totalQuestions,
        responses: viewAssessment.totalSubmittedResponses,
        uniqueKey: viewAssessment.assessmentUID,
    } : null;

    // const mappedMailAssessment = mailAssessment ? {
    //     ...mailAssessment,
    //     uniqueKey: mailAssessment.assessmentUID,
    // } : null;

    return (
        <div className="min-h-screen bg-slate-50 p-0.5">
            <div className="max-w-[88rem] mx-auto">
                <div className="flex items-stretch gap-1.5 mb-2.5">
                    <div className="hidden md:block w-72 lg:w-80 xl:w-96 shrink-0">
                        <img src={image} loading="lazy" alt="Dashboard-Image" className="w-full h-full object-contain object-left" />
                    </div>
                    <div className="flex-1 flex flex-col gap-3 min-w-0 py-1">
                        <div>
                            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Assigned Assessments</h1>
                            <p className="text-gray-500 mt-1 text-sm lg:text-base">
                                Assessments assigned to you, at a glance.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-0.5">
                            {loading ? <StatCardSkeleton /> : (
                                <StatCard
                                    icon={FileText}
                                    iconBg="bg-[#7f35b2]/10"
                                    iconColor="text-[#7f35b2]"
                                    label="Total Assigned"
                                    value={withStatus.length}
                                    subtitle="All assigned assessments"
                                />
                            )}
                            {loading ? <StatCardSkeleton /> : (
                                <StatCard
                                    icon={BarChart3}
                                    iconBg="bg-green-100"
                                    iconColor="text-green-600"
                                    label="Active"
                                    value={getTabCount("Active")}
                                    subtitle="Currently open"
                                />
                            )}
                            {loading ? <StatCardSkeleton /> : (
                                <StatCard
                                    icon={Users}
                                    iconBg="bg-blue-100"
                                    iconColor="text-blue-600"
                                    label="Total Responses"
                                    value={totalResponses}
                                    subtitle="Across all assigned"
                                />
                            )}
                            {loading ? <StatCardSkeleton /> : (
                                <StatCard
                                    icon={CheckCircle2}
                                    iconBg="bg-purple-100"
                                    iconColor="text-purple-600"
                                    label="Completed"
                                    value={getTabCount("Completed")}
                                    subtitle="Window closed"
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-1.5 px-4 py-0.5">
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                        <div className="flex items-center gap-1 overflow-x-auto">
                            {STATUS_TABS.map(({ key, label, icon: Icon }) => {
                                const isActive = activeTab === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => setActiveTab(key)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-[#7f35b2]/10 text-[#7f35b2]" : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                                            }`}
                                    >
                                        <Icon size={16} />
                                        {label} ({getTabCount(key)})
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-2 py-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search assessments..."
                                    className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-full sm:w-56 focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
                                />
                            </div>
                           
                                
                            
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-gray-500">
                                    <th className="px-4 py-3 font-medium min-w-[240px] w-[28%]">Assessment Details</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 font-medium text-center">Questions</th>
                                    <th className="px-4 py-3 font-medium text-center">Responses</th>
                                    <th className="px-4 py-3 font-medium">Assessment Window</th>
                                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)}

                                {!loading && paginated.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-16">
                                            <div className="text-center">
                                                <div className="w-16 h-16 mx-auto bg-[#7f35b2]/10 rounded-full flex items-center justify-center">
                                                    <FileText className="text-[#7f35b2]" size={30} />
                                                </div>
                                                <h3 className="text-lg font-semibold mt-4">No Assessments Found</h3>
                                                <p className="text-gray-500 mt-1 text-sm">Nothing assigned to you in this category yet.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    paginated.map((assessment) => {
                                        const start = formatDateParts(assessment.startDateTime);
                                        const end = formatDateParts(assessment.endDateTime);

                                        return (
                                            <tr key={assessment.assessmentUID} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                                                <td className="px-4 py-3">
                                                    <div
                                                        className="flex items-start gap-3 cursor-pointer group"
                                                        onClick={() => setViewAssessment(assessment)}
                                                        title="View Details"
                                                    >
                                                        <div className="bg-[#7f35b2]/10 p-2 rounded-lg shrink-0">
                                                            <FileText className="text-[#7f35b2]" size={18} />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-gray-900 group-hover:text-[#7f35b2] group-hover:underline">{assessment.assessmentName}</p>
                                                            <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                                            <div
                                                                className="description-rich-content text-gray-500 text-xs mt-0.5 line-clamp-2"
                                                                title={stripHtml(assessment.description)}
                                                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(assessment.description) }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(assessment._status)}`}>
                                                        {assessment._status}
                                                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                                    </span>
                                                </td>

                                                <td className="px-4 py-3 text-center text-gray-700">{assessment.totalQuestions ?? "-"}</td>
                                                <td className="px-4 py-3 text-center text-gray-700">{assessment.totalSubmittedResponses ?? 0}</td>

                                                <td className="px-4 py-3 text-gray-700">
                                                    <div>{start.date} {start.time}</div>
                                                    <div className="text-xs text-gray-400">to {end.date} {end.time}</div>
                                                </td>

                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => setMailAssessment(assessment)}
                                                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-purple-600"
                                                            title="Send Mail"
                                                        >
                                                            <Mail size={16} />
                                                        </button>

                                                        <button
                                                            onClick={() => handlePreview(assessment)}
                                                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-blue-600"
                                                            title="Preview"
                                                        >
                                                            <Monitor size={16} />
                                                        </button>
                                                        {(assessment._status === "Active" || assessment._status === "Scheduled") && (
                                                            <button
                                                                onClick={() => setResponderAssessment(assessment)}
                                                                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-purple-600"
                                                                title="Add User"
                                                            >
                                                                <UserPlus size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>

                    {!loading && filtered.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
                            <p className="text-sm text-gray-500">
                                Showing {startIndex + 1} to {Math.min(startIndex + pageSize, filtered.length)} of {filtered.length} assessments
                            </p>
                            <div className="flex items-center gap-1.5">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                                    <ChevronsLeft size={16} />
                                </button>
                                <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                                    <ChevronLeft size={16} />
                                </button>
                                {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                                    page === "..." ? (
                                        <span key={`ellipsis-${idx}`} className="px-1 text-gray-400 text-sm">...</span>
                                    ) : (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-8 h-8 rounded-lg text-sm font-medium ${page === currentPage ? "bg-[#7f35b2] text-white" : "border border-gray-200 text-gray-700 hover:bg-gray-50"}`}
                                        >
                                            {page}
                                        </button>
                                    )
                                )}
                                <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                                    <ChevronRight size={16} />
                                </button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
                                    <ChevronsRight size={16} />
                                </button>
                                <select
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                                    className="border border-gray-200 rounded-lg text-sm px-2 py-1.5 ml-1"
                                >
                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>{size} / page</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ViewAssessmentModal
                isOpen={viewAssessment !== null}
                assessment={mappedAssessment}
                onClose={() => setViewAssessment(null)}
            />
            <PublishResponderModal
                isOpen={responderAssessment !== null}
                assessment={responderAssessment}
                onClose={() => setResponderAssessment(null)}
                onSuccess={loadAssessments}
            />
            <SendAssessmentMailModal
                isOpen={mailAssessment !== null}
                assessment={mailAssessment}
                onClose={() => setMailAssessment(null)}
                onSend={handleSendMail}
            />
        </div>
    );
};

export default AssessmentList;